/**
 * What a home card may carry beyond its stock shapes, read strictly: a little CSS (`style`), a
 * little HTML (`template`), a sandboxed script (`script`), and forms that name a screen's declared
 * action (`forms`). Pure — no DOM, no network, no dependency — so the same reading runs where a
 * card is proposed (the assistant), where it is kept (identity, through `validateHomeCard`) and
 * where it is drawn (the SDK), and all three agree on what survives.
 *
 * Every reader here is an allowlist: what it does not know it drops, and says what and why
 * (`Stripped`), so the assistant can tell the person «حُذف … لأن …». Nothing is repaired into
 * something else; a part that cannot be read at all is dropped whole.
 *
 * - CSS is tokenised by hand (no CSS parser is a dependency of the browser package): rules of
 *   simple selectors and declarations of known properties whose values are checked token by token
 *   and held inside bounds. No at-rule, no string, no escape, no `url()`, no position that leaves
 *   the card, no transform, no z-index.
 * - HTML is parsed into a tree of a closed set of tags with three attributes; values come only from
 *   `{{field}}`, which the renderer writes as text. No link, image, frame, script, style or event.
 * - A script is never run in the page: the SDK draws it in `<iframe sandbox="allow-scripts">`
 *   under a CSP with no network. Here it is only held to its size and kept from closing its tag.
 */
export const CARD_MARKUP_LIMITS = {
    /** Characters, as the schema caps them (a schema test holds the two equal). */
    style: 4096,
    template: 8192,
    script: 16384,
    rules: 80,
    declarations: 30,
    nodes: 400,
    depth: 12,
    classes: 12,
    forms: 3,
    sources: 4,
};
const quote = (s) => {
    const t = s.replace(/\s+/g, ' ').trim();
    return t.length > 60 ? `${t.slice(0, 57)}…` : t;
};
// ── CSS ─────────────────────────────────────────────────────────────────────────────────────
/** The tags a template may use; also the only type selectors a style may name. */
export const TEMPLATE_TAGS = new Set([
    'b', 'strong', 'i', 'em', 'small', 'mark', 'span', 'p', 'div', 'br', 'hr', 'h3', 'h4', 'h5',
    'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
]);
/** A value's tokens, or null when it holds anything this reader does not know. */
function tokens(value) {
    let i = 0;
    const read = (inFn) => {
        const out = [];
        while (i < value.length) {
            const c = value[i];
            if (/\s/.test(c)) {
                i += 1;
                continue;
            }
            if (c === ')')
                return inFn ? out : null;
            if (c === ',') {
                out.push({ t: 'comma' });
                i += 1;
                continue;
            }
            if (c === '/') {
                out.push({ t: 'slash' });
                i += 1;
                continue;
            }
            if (c === '#') {
                const m = /^#([0-9a-fA-F]+)/.exec(value.slice(i));
                if (!m || ![3, 4, 6, 8].includes(m[1].length))
                    return null;
                out.push({ t: 'hash', v: m[1].toLowerCase() });
                i += m[0].length;
                continue;
            }
            const num = /^([+-]?(?:\d+\.?\d*|\.\d+))(px|rem|em|%|fr|deg)?(?![A-Za-z0-9_-])/.exec(value.slice(i));
            if (num) {
                out.push({ t: 'num', n: Number(num[1]), unit: num[2] ?? '' });
                i += num[0].length;
                continue;
            }
            const id = /^(-{0,2}[A-Za-z_][A-Za-z0-9_-]*)/.exec(value.slice(i));
            if (id) {
                i += id[0].length;
                if (value[i] === '(') {
                    i += 1;
                    const inner = read(true);
                    if (inner === null || value[i] !== ')')
                        return null;
                    i += 1;
                    const args = [[]];
                    for (const tk of inner) {
                        if (tk.t === 'comma')
                            args.push([]);
                        else
                            args[args.length - 1].push(tk);
                    }
                    out.push({ t: 'fn', name: id[1].toLowerCase(), args });
                    continue;
                }
                out.push({ t: 'ident', v: id[1].toLowerCase() });
                continue;
            }
            return null;
        }
        return inFn ? null : out;
    };
    return read(false);
}
const NAMED_COLORS = new Set(['transparent', 'currentcolor', 'inherit', 'white', 'black', 'red', 'green', 'blue', 'gray', 'grey',
    'orange', 'yellow', 'purple', 'teal', 'navy', 'maroon', 'olive', 'silver', 'lime', 'aqua', 'fuchsia']);
const THEME_VAR = /^--[a-z][a-z0-9-]{0,40}$/;
function isColor(tk) {
    if (tk.t === 'hash')
        return true;
    if (tk.t === 'ident')
        return NAMED_COLORS.has(tk.v);
    if (tk.t !== 'fn')
        return false;
    if (tk.name === 'var') {
        // A theme variable, so the card follows light and dark: `var(--tone-red)`, with at most a colour to fall back on.
        const [name, fallback, extra] = tk.args;
        if (extra || !name || name.length !== 1 || name[0].t !== 'ident' || !THEME_VAR.test(name[0].v))
            return false;
        return !fallback || (fallback.length === 1 && isColor(fallback[0]));
    }
    if (['rgb', 'rgba', 'hsl', 'hsla'].includes(tk.name)) {
        const flat = tk.args.flat().filter((x) => x.t !== 'slash');
        return flat.length >= 3 && flat.length <= 4 && flat.every((x) => x.t === 'num' && ['', '%', 'deg'].includes(x.unit) && x.n >= 0 && x.n <= 360);
    }
    return false;
}
/** A length inside bounds: px up to `px`, rem/em up to px/16, % up to `pct`; nothing negative; a bare 0. */
function lengthOk(tk, px, pct = 100) {
    if (tk.t !== 'num')
        return 'value';
    if (tk.unit === '' && tk.n === 0)
        return true;
    if (tk.n < 0)
        return 'bounds';
    if (tk.unit === 'px')
        return tk.n <= px ? true : 'bounds';
    if (tk.unit === 'rem' || tk.unit === 'em')
        return tk.n <= px / 16 ? true : 'bounds';
    if (tk.unit === '%')
        return tk.n <= pct ? true : 'bounds';
    return 'value';
}
const oneOf = (...words) => (toks) => (toks.length === 1 && toks[0].t === 'ident' && words.includes(toks[0].v) ? true : 'value');
const colorCheck = (toks) => (toks.length === 1 && isColor(toks[0]) ? true : 'value');
const firstBad = (results) => results.find((r) => r !== true) ?? true;
/** 1 to `max` lengths, or the words given. */
const lengths = (px, max, words = [], pct = 100) => (toks) => {
    if (!toks.length || toks.length > max)
        return 'value';
    return firstBad(toks.map((tk) => (tk.t === 'ident' && words.includes(tk.v) ? true : lengthOk(tk, px, pct))));
};
const number = (lo, hi, integer = false) => (toks) => {
    if (toks.length !== 1 || toks[0].t !== 'num' || toks[0].unit !== '')
        return 'value';
    const n = toks[0].n;
    if (integer && !Number.isInteger(n))
        return 'value';
    return n >= lo && n <= hi ? true : 'bounds';
};
const SIZE_WORDS = ['auto', 'min-content', 'max-content', 'fit-content'];
const size = (extra = []) => (toks) => (toks.length === 1
    ? (toks[0].t === 'ident' && [...SIZE_WORDS, ...extra].includes(toks[0].v) ? true : lengthOk(toks[0], 1200))
    : 'value');
const BORDER_STYLES = ['none', 'solid', 'dashed', 'dotted', 'double'];
const borderWidth = (tk) => (tk.t === 'ident' && ['thin', 'medium', 'thick'].includes(tk.v) ? true : lengthOk(tk, 8));
const border = (toks) => {
    if (!toks.length || toks.length > 3)
        return 'value';
    return firstBad(toks.map((tk) => (tk.t === 'ident' && BORDER_STYLES.includes(tk.v) ? true : isColor(tk) ? true : borderWidth(tk))));
};
/** A grid track: a length, a fraction, a sizing word, `minmax(a, b)`, or `repeat(n, …)` of them. */
function trackOk(tk, inRepeat = false) {
    if (tk.t === 'ident')
        return ['auto', 'min-content', 'max-content'].includes(tk.v) ? true : 'value';
    if (tk.t === 'num' && tk.unit === 'fr')
        return tk.n >= 0 && tk.n <= 12 ? true : 'bounds';
    if (tk.t === 'num')
        return lengthOk(tk, 1200);
    if (tk.t === 'fn' && tk.name === 'minmax' && tk.args.length === 2 && tk.args.every((a) => a.length === 1))
        return firstBad(tk.args.map((a) => trackOk(a[0], true)));
    if (tk.t === 'fn' && tk.name === 'repeat' && !inRepeat && tk.args.length === 2) {
        const [n, list] = tk.args;
        if (!n || n.length !== 1 || n[0].t !== 'num' || n[0].unit !== '' || !Number.isInteger(n[0].n))
            return 'value';
        if (n[0].n < 1 || n[0].n > 12)
            return 'bounds';
        if (!list?.length || list.length > 4)
            return 'value';
        return firstBad(list.map((x) => trackOk(x, true)));
    }
    return 'value';
}
const tracks = (toks) => {
    if (toks.length === 1 && toks[0].t === 'ident' && toks[0].v === 'none')
        return true;
    if (!toks.length || toks.length > 12)
        return 'value';
    return firstBad(toks.map((tk) => trackOk(tk)));
};
/** `auto`, a line 1–13, `span n`, and one `/` between two of them. */
const gridLine = (toks) => {
    const parts = [[]];
    for (const tk of toks) {
        if (tk.t === 'slash')
            parts.push([]);
        else
            parts[parts.length - 1].push(tk);
    }
    if (parts.length > 2)
        return 'value';
    return firstBad(parts.map((p) => {
        if (p.length === 1 && p[0].t === 'ident' && p[0].v === 'auto')
            return true;
        const n = p.length === 2 && p[0].t === 'ident' && p[0].v === 'span' ? p[1] : p.length === 1 ? p[0] : undefined;
        if (!n || n.t !== 'num' || n.unit !== '' || !Number.isInteger(n.n))
            return 'value';
        return n.n >= 1 && n.n <= 13 ? true : 'bounds';
    }));
};
const flex = (toks) => {
    if (toks.length === 1 && toks[0].t === 'ident')
        return ['none', 'auto'].includes(toks[0].v) ? true : 'value';
    if (!toks.length || toks.length > 3)
        return 'value';
    return firstBad(toks.map((tk, i) => (i < 2 ? number(0, 12)([tk]) : tk.t === 'ident' && tk.v === 'auto' ? true : lengthOk(tk, 1200))));
};
const ALIGN = ['start', 'end', 'flex-start', 'flex-end', 'center', 'baseline', 'stretch', 'normal', 'space-between', 'space-around', 'space-evenly'];
const box = (px, words = []) => lengths(px, 4, words, 50);
const boxSide = (px, words = []) => lengths(px, 1, words, 50);
const boxAxis = (px, words = []) => lengths(px, 2, words, 50);
/** Every property a card's style may set, and what its value may be. */
const PROPERTIES = (() => {
    const p = {
        color: colorCheck, 'background-color': colorCheck, background: colorCheck,
        'border-color': (toks) => (toks.length >= 1 && toks.length <= 4 && toks.every(isColor) ? true : 'value'),
        'text-decoration-color': colorCheck,
        'font-weight': (toks) => (toks.length === 1 && toks[0].t === 'ident' ? oneOf('normal', 'bold', 'bolder', 'lighter')(toks)
            : toks.length === 1 && toks[0].t === 'num' && toks[0].unit === '' && Number.isInteger(toks[0].n / 100) ? number(100, 900)(toks) : 'value'),
        'font-size': (toks) => (toks.length === 1 && toks[0].t === 'ident' ? oneOf('small', 'medium', 'large', 'x-large', 'smaller', 'larger')(toks)
            : toks.length === 1 && toks[0].t === 'num' && toks[0].unit === '%' ? (toks[0].n >= 50 && toks[0].n <= 300 ? true : 'bounds')
                : toks.length === 1 && toks[0].t === 'num' && ['px', 'rem', 'em'].includes(toks[0].unit)
                    ? ((toks[0].unit === 'px' ? toks[0].n >= 8 && toks[0].n <= 48 : toks[0].n >= 0.5 && toks[0].n <= 3) ? true : 'bounds') : 'value'),
        'font-style': oneOf('normal', 'italic'),
        'font-variant-numeric': oneOf('normal', 'tabular-nums', 'lining-nums'),
        'text-align': oneOf('start', 'end', 'left', 'right', 'center', 'justify'),
        'text-decoration': oneOf('none', 'underline', 'line-through'),
        'text-decoration-line': oneOf('none', 'underline', 'line-through'),
        'text-transform': oneOf('none', 'uppercase', 'lowercase', 'capitalize'),
        'line-height': (toks) => (toks.length === 1 && toks[0].t === 'ident' ? oneOf('normal')(toks)
            : toks.length === 1 && toks[0].t === 'num' && toks[0].unit === '' ? number(0.8, 3)(toks)
                : toks.length === 1 ? lengthOk(toks[0], 72, 300) : 'value'),
        'letter-spacing': (toks) => (toks.length === 1 && toks[0].t === 'ident' ? oneOf('normal')(toks) : toks.length === 1 ? lengthOk(toks[0], 10) : 'value'),
        'vertical-align': oneOf('top', 'middle', 'bottom', 'baseline', 'text-top', 'text-bottom'),
        'white-space': oneOf('normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line'),
        'word-break': oneOf('normal', 'break-all', 'keep-all', 'break-word'),
        'overflow-wrap': oneOf('normal', 'anywhere', 'break-word'),
        'text-overflow': oneOf('clip', 'ellipsis'),
        padding: box(64), margin: box(64, ['auto']),
        border, 'border-width': (toks) => (toks.length >= 1 && toks.length <= 4 ? firstBad(toks.map(borderWidth)) : 'value'),
        'border-style': (toks) => (toks.length >= 1 && toks.length <= 4 && toks.every((tk) => tk.t === 'ident' && BORDER_STYLES.includes(tk.v)) ? true : 'value'),
        'border-radius': lengths(32, 4, [], 50),
        'border-collapse': oneOf('collapse', 'separate'),
        'border-spacing': lengths(16, 2),
        display: oneOf('block', 'inline', 'inline-block', 'flex', 'inline-flex', 'grid', 'inline-grid', 'none'),
        'flex-direction': oneOf('row', 'row-reverse', 'column', 'column-reverse'),
        'flex-wrap': oneOf('nowrap', 'wrap', 'wrap-reverse'),
        flex, 'flex-grow': number(0, 12), 'flex-shrink': number(0, 12), 'flex-basis': size(),
        order: number(-12, 12, true),
        'justify-content': oneOf(...ALIGN), 'align-items': oneOf(...ALIGN), 'align-self': oneOf('auto', ...ALIGN),
        'align-content': oneOf(...ALIGN), 'justify-items': oneOf(...ALIGN), 'justify-self': oneOf('auto', ...ALIGN),
        'place-items': oneOf(...ALIGN), 'place-content': oneOf(...ALIGN),
        gap: lengths(64, 2), 'row-gap': lengths(64, 1), 'column-gap': lengths(64, 1),
        'grid-template-columns': tracks, 'grid-template-rows': tracks, 'grid-auto-rows': tracks, 'grid-auto-columns': tracks,
        'grid-column': gridLine, 'grid-row': gridLine,
        'grid-column-start': gridLine, 'grid-column-end': gridLine, 'grid-row-start': gridLine, 'grid-row-end': gridLine,
        'grid-auto-flow': (toks) => (toks.length >= 1 && toks.length <= 2 && toks.every((tk) => tk.t === 'ident' && ['row', 'column', 'dense'].includes(tk.v)) ? true : 'value'),
        width: size(), height: size(), 'min-width': size(), 'min-height': size(), 'max-width': size(['none']), 'max-height': size(['none']),
        'inline-size': size(), 'block-size': size(),
        'box-sizing': oneOf('border-box', 'content-box'),
        opacity: (toks) => (toks.length === 1 && toks[0].t === 'num' && toks[0].unit === '%' ? (toks[0].n <= 100 && toks[0].n >= 0 ? true : 'bounds') : number(0, 1)(toks)),
        overflow: oneOf('visible', 'hidden', 'auto', 'clip', 'scroll'),
        'overflow-x': oneOf('visible', 'hidden', 'auto', 'clip', 'scroll'),
        'overflow-y': oneOf('visible', 'hidden', 'auto', 'clip', 'scroll'),
        position: oneOf('static', 'relative'),
        'list-style-type': oneOf('none', 'disc', 'circle', 'square', 'decimal'),
        'list-style': (toks) => (toks.length >= 1 && toks.length <= 2 && toks.every((tk) => tk.t === 'ident' && ['none', 'disc', 'circle', 'square', 'decimal', 'inside', 'outside'].includes(tk.v)) ? true : 'value'),
        'list-style-position': oneOf('inside', 'outside'),
        'table-layout': oneOf('auto', 'fixed'),
        'caption-side': oneOf('top', 'bottom'),
    };
    for (const side of ['top', 'right', 'bottom', 'left', 'inline-start', 'inline-end', 'block-start', 'block-end']) {
        p[`padding-${side}`] = boxSide(64);
        p[`margin-${side}`] = boxSide(64, ['auto']);
        p[`border-${side}`] = border;
        p[`border-${side}-color`] = colorCheck;
        p[`border-${side}-width`] = (toks) => (toks.length === 1 ? borderWidth(toks[0]) : 'value');
        p[`border-${side}-style`] = oneOf(...BORDER_STYLES);
    }
    for (const axis of ['inline', 'block']) {
        p[`padding-${axis}`] = boxAxis(64);
        p[`margin-${axis}`] = boxAxis(64, ['auto']);
    }
    return p;
})();
/** Words that never reach a property check: anything that fetches, runs, or is not CSS at all. */
const FETCHES = /\b(?:url|image-set|image|attr|element|src|env|expression|cross-fade|paint)\s*\(|javascript:|-moz-binding|behavior\s*:/i;
/** One simple selector list, or null: tags of the template's set, classes, and a few structural pseudo-classes. */
function selectorOk(selector) {
    const list = selector.split(',').map((s) => s.trim());
    if (!list.length || list.some((s) => !s || s.length > 200))
        return false;
    const compound = /^(?:\*|[a-z][a-z0-9]*)?(?:\.-?[A-Za-z_][A-Za-z0-9_-]*)*(?::(?:first-child|last-child|only-child|hover|nth-child\((?:odd|even|\d{1,2}|\d{0,2}n(?:\+\d{1,2})?)\)))*$/;
    for (const s of list) {
        const parts = s.replace(/\s*([>+~])\s*/g, ' $1 ').split(/\s+/);
        let expectCompound = true;
        for (const part of parts) {
            if (['>', '+', '~'].includes(part)) {
                if (expectCompound)
                    return false;
                expectCompound = true;
                continue;
            }
            if (!part || !compound.test(part))
                return false;
            const tag = /^([a-z][a-z0-9]*)/.exec(part)?.[1];
            if (tag && !TEMPLATE_TAGS.has(tag))
                return false;
            expectCompound = false;
        }
        if (expectCompound)
            return false;
    }
    return true;
}
/**
 * A card's style, read: the rules kept, written back in one canonical form, and what was dropped.
 * An unreadable sheet (a string, an escape, an unclosed block) is dropped whole.
 */
export function cleanCardStyle(raw) {
    const stripped = [];
    const drop = (what, why) => stripped.push({ part: 'style', what: quote(what), why });
    if (raw.length > CARD_MARKUP_LIMITS.style) {
        drop(`${raw.length} characters`, 'too_big');
        return { css: '', stripped };
    }
    // No strings, no escapes, no markup, no nesting: each could hide a word from the checks below.
    if (/[\\"'<&]/.test(raw)) {
        drop(raw, 'unreadable');
        return { css: '', stripped };
    }
    const text = raw.replace(/\/\*[\s\S]*?\*\//g, ' ');
    if (text.includes('/*') || text.includes('*/')) {
        drop(raw, 'unreadable');
        return { css: '', stripped };
    }
    const kept = [];
    let i = 0;
    while (i < text.length) {
        while (i < text.length && /\s/.test(text[i]))
            i += 1;
        if (i >= text.length)
            break;
        if (text[i] === '@') {
            // Every at-rule: @import and @font-face fetch; @media and the rest are refused rather than half-read.
            const name = /^@[A-Za-z-]*/.exec(text.slice(i))[0];
            const semi = text.indexOf(';', i);
            const open = text.indexOf('{', i);
            if (open !== -1 && (semi === -1 || open < semi)) {
                let depth = 0;
                let j = open;
                for (; j < text.length; j += 1) {
                    if (text[j] === '{')
                        depth += 1;
                    else if (text[j] === '}') {
                        depth -= 1;
                        if (depth === 0)
                            break;
                    }
                }
                if (depth !== 0) {
                    drop(raw, 'unreadable');
                    return { css: '', stripped };
                }
                i = j + 1;
            }
            else
                i = semi === -1 ? text.length : semi + 1;
            drop(name, 'at_rule');
            continue;
        }
        const open = text.indexOf('{', i);
        const close = open === -1 ? -1 : text.indexOf('}', open);
        if (open === -1 || close === -1 || text.slice(i, open).includes('}') || text.slice(open + 1, close).includes('{')) {
            drop(raw, 'unreadable');
            return { css: '', stripped };
        }
        const selector = text.slice(i, open).trim().replace(/\s+/g, ' ');
        const body = text.slice(open + 1, close);
        i = close + 1;
        if (kept.length >= CARD_MARKUP_LIMITS.rules) {
            drop(selector, 'too_big');
            continue;
        }
        if (!selectorOk(selector)) {
            drop(selector, 'selector');
            continue;
        }
        const decls = [];
        for (const piece of body.split(';')) {
            const d = piece.trim();
            if (!d)
                continue;
            const colon = d.indexOf(':');
            const prop = (colon === -1 ? d : d.slice(0, colon)).trim().toLowerCase();
            let value = colon === -1 ? '' : d.slice(colon + 1).trim();
            if (colon === -1 || !/^[a-z-]+$/.test(prop) || !value) {
                drop(d, 'unreadable');
                continue;
            }
            if (FETCHES.test(value)) {
                drop(d, 'url');
                continue;
            }
            if (/!\s*important$/i.test(value)) {
                drop(d, 'important');
                continue;
            }
            value = value.replace(/\s+/g, ' ');
            if (prop === 'position' && /^(fixed|absolute|sticky)$/i.test(value)) {
                drop(d, 'position');
                continue;
            }
            const check = PROPERTIES[prop];
            if (!check) {
                drop(d, 'property');
                continue;
            }
            const toks = tokens(value);
            const verdict = toks ? check(toks) : 'value';
            if (verdict !== true) {
                drop(d, verdict);
                continue;
            }
            if (decls.length >= CARD_MARKUP_LIMITS.declarations) {
                drop(d, 'too_big');
                continue;
            }
            decls.push(`${prop}: ${value}`);
        }
        if (decls.length)
            kept.push(`${selector} { ${decls.join('; ')} }`);
    }
    return { css: kept.join('\n'), stripped };
}
/** What a template may name outside a loop: the card's figure, its counts, its title. */
export const TEMPLATE_TOP = ['value', 'count', 'more', 'total', 'title'];
/** What a template may name inside `{{#each items}}` (the bars of a bars card, the points of a trend). */
export const TEMPLATE_ITEM = ['key', 'label', 'value'];
const FIELD = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;
const ACTION = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
/** Dropped with everything inside: their content is code, markup of another kind, or a control. */
const WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'svg', 'math', 'template', 'textarea', 'title', 'noscript',
    'select', 'button', 'form', 'frameset', 'frame', 'audio', 'video', 'canvas', 'noembed', 'xmp', 'plaintext']);
const RAW_TEXT = new Set(['script', 'style', 'textarea', 'title', 'xmp', 'noscript', 'noembed', 'plaintext']);
const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
function decode(text) {
    return text.replace(/&(#x[0-9a-fA-F]{1,6}|#\d{1,7}|[a-z]+);/g, (whole, body) => {
        if (body[0] === '#') {
            const code = body[1] === 'x' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
            return code >= 32 && code <= 0x10ffff && !(code >= 0x7f && code < 0xa0) ? String.fromCodePoint(code) : '';
        }
        return ENTITIES[body] ?? whole;
    });
}
/**
 * A card's template, read into a tree the renderer draws with its own elements (never as HTML).
 * `shape` says what a loop may run over: a table's `rows`, a bars or trend card's `items`.
 * `forms` are the actions `{{action …}}` may place. Anything else is dropped and said; a template
 * that is not well formed — a tag left open, a close with no open — is dropped whole.
 */
export function cleanCardTemplate(raw, shape, forms = []) {
    const stripped = [];
    const drop = (what, why) => { if (stripped.length < 40)
        stripped.push({ part: 'template', what: quote(what), why }); };
    const whole = (why) => ({ nodes: [], fields: [], stripped: [{ part: 'template', what: quote(raw), why }] });
    if (raw.length > CARD_MARKUP_LIMITS.template)
        return whole('too_big');
    const root = { tag: '#root', kids: [], keep: true };
    const stack = [root];
    const fields = new Set();
    let nodes = 0;
    let i = 0;
    const top = () => stack[stack.length - 1];
    const loop = () => [...stack].reverse().find((f) => f.each)?.each;
    const add = (n) => {
        nodes += 1;
        if (nodes > CARD_MARKUP_LIMITS.nodes)
            throw new Error('too_big');
        top().kids.push(n);
    };
    const text = (s) => { if (!s)
        return; const last = top().kids.at(-1); if (last?.k === 'text')
        last.v += s;
    else
        add({ k: 'text', v: s }); };
    try {
        while (i < raw.length) {
            if (raw.startsWith('{{', i)) {
                const end = raw.indexOf('}}', i + 2);
                if (end === -1)
                    return whole('unreadable');
                const expr = raw.slice(i + 2, end).trim();
                i = end + 2;
                const each = /^#each\s+(\S+)$/.exec(expr);
                if (each) {
                    const over = each[1];
                    const allowed = shape === 'table' ? 'rows' : shape === 'bars' || shape === 'trend' ? 'items' : null;
                    if (loop())
                        return whole('nesting');
                    if (over !== allowed) {
                        drop(`{{${expr}}}`, 'interpolation');
                        stack.push({ tag: '#each', kids: [], keep: false, each: 'rows' });
                        continue;
                    }
                    if (stack.length > CARD_MARKUP_LIMITS.depth)
                        return whole('nesting');
                    stack.push({ tag: '#each', kids: [], keep: true, each: over });
                    continue;
                }
                if (expr === '/each') {
                    const f = stack.pop();
                    if (!f || f.tag !== '#each')
                        return whole('unreadable');
                    if (f.keep)
                        add({ k: 'each', over: f.each, kids: f.kids });
                    continue;
                }
                const action = /^action\s+(\S+)$/.exec(expr);
                if (action) {
                    if (ACTION.test(action[1]) && forms.includes(action[1]))
                        add({ k: 'action', target: action[1] });
                    else
                        drop(`{{${expr}}}`, 'target');
                    continue;
                }
                const inLoop = loop();
                const ok = inLoop === 'rows' ? FIELD.test(expr)
                    : inLoop === 'items' ? TEMPLATE_ITEM.includes(expr)
                        : TEMPLATE_TOP.includes(expr);
                if (!ok) {
                    drop(`{{${expr}}}`, 'interpolation');
                    continue;
                }
                if (inLoop === 'rows' && stack.every((f) => f.keep))
                    fields.add(expr);
                add({ k: 'field', path: expr });
                continue;
            }
            if (raw.startsWith('<!--', i)) {
                const end = raw.indexOf('-->', i + 4);
                if (end === -1)
                    return whole('unreadable');
                i = end + 3;
                continue;
            }
            if (raw[i] === '<' && /[A-Za-z/!?]/.test(raw[i + 1] ?? '')) {
                if (raw[i + 1] === '!' || raw[i + 1] === '?')
                    return whole('unreadable');
                const close = raw[i + 1] === '/';
                const m = /^<\/?([A-Za-z][A-Za-z0-9-]*)/.exec(raw.slice(i));
                if (!m)
                    return whole('unreadable');
                const tag = m[1].toLowerCase();
                let j = i + m[0].length;
                const attrs = [];
                for (;;) {
                    while (j < raw.length && /\s/.test(raw[j]))
                        j += 1;
                    if (raw[j] === '>' || raw.startsWith('/>', j))
                        break;
                    if (close)
                        return whole('unreadable');
                    const a = /^([A-Za-z_:][-A-Za-z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/.exec(raw.slice(j));
                    if (!a)
                        return whole('unreadable');
                    attrs.push([a[1].toLowerCase(), a[2] ?? a[3] ?? a[4] ?? null]);
                    j += a[0].length;
                }
                const selfClosing = raw.startsWith('/>', j);
                i = j + (selfClosing ? 2 : 1);
                if (close) {
                    const f = stack.pop();
                    if (!f || f.tag !== tag)
                        return whole('unreadable');
                    if (f.keep)
                        add({ k: 'el', tag, attrs: f.attrs ?? {}, kids: f.kids });
                    else if (!WITH_CONTENT.has(tag))
                        for (const kid of f.kids)
                            add(kid);
                    continue;
                }
                if (RAW_TEXT.has(tag)) {
                    // Its content is not markup: skipped to its own close, and dropped with it.
                    const end = raw.toLowerCase().indexOf(`</${tag}`, i);
                    if (end === -1)
                        return whole('unreadable');
                    const after = raw.indexOf('>', end);
                    if (after === -1)
                        return whole('unreadable');
                    i = after + 1;
                    drop(`<${tag}>`, 'tag');
                    continue;
                }
                const allowed = TEMPLATE_TAGS.has(tag);
                if (!allowed)
                    drop(`<${tag}>`, tag === 'a' || tag === 'img' || tag === 'link' ? 'url' : 'tag');
                const kept = {};
                if (allowed) {
                    for (const [name, value] of attrs) {
                        if (name === 'class' && value !== null) {
                            const list = value.trim().split(/\s+/).filter(Boolean);
                            if (list.length <= CARD_MARKUP_LIMITS.classes && list.every((c) => /^-?[A-Za-z_][A-Za-z0-9_-]{0,39}$/.test(c))) {
                                if (list.length)
                                    kept.class = list.join(' ');
                                continue;
                            }
                            drop(`class="${value}"`, 'attribute');
                            continue;
                        }
                        if (name === 'dir' && (value === 'ltr' || value === 'rtl' || value === 'auto')) {
                            kept.dir = value;
                            continue;
                        }
                        if ((name === 'colspan' || name === 'rowspan') && (tag === 'td' || tag === 'th') && value !== null && /^\d{1,2}$/.test(value) && Number(value) >= 1 && Number(value) <= 12) {
                            kept[name] = Number(value);
                            continue;
                        }
                        drop(value === null ? name : `${name}="${value}"`, /^on/.test(name) ? 'attribute' : ['href', 'src', 'srcset', 'action', 'formaction', 'xlink:href', 'poster', 'background'].includes(name) ? 'url' : 'attribute');
                    }
                }
                if (VOID.has(tag) || selfClosing) {
                    if (allowed && VOID.has(tag))
                        add({ k: 'el', tag, attrs: kept, kids: [] });
                    else if (allowed)
                        return whole('unreadable');
                    continue;
                }
                if (stack.length > CARD_MARKUP_LIMITS.depth)
                    return whole('nesting');
                stack.push({ tag, kids: [], keep: allowed && stack.every((f) => f.keep || !WITH_CONTENT.has(f.tag)), attrs: kept });
                continue;
            }
            // Text, to the next tag or value.
            let end = i + 1;
            while (end < raw.length && !raw.startsWith('{{', end) && !(raw[end] === '<' && /[A-Za-z/!?]/.test(raw[end + 1] ?? '')))
                end += 1;
            const inDropped = stack.some((f) => !f.keep && WITH_CONTENT.has(f.tag));
            if (!inDropped)
                text(decode(raw.slice(i, end)));
            i = end;
        }
    }
    catch {
        return whole('too_big');
    }
    if (stack.length !== 1)
        return whole('unreadable');
    return { nodes: root.kids, fields: [...fields], stripped };
}
const escapeText = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\{\{/g, '{&#123;');
/** A template tree written back as a template: what the assistant keeps once something was dropped from it. */
export function writeTemplate(nodes) {
    return nodes.map((n) => {
        switch (n.k) {
            case 'text': return escapeText(n.v);
            case 'field': return `{{${n.path}}}`;
            case 'action': return `{{action ${n.target}}}`;
            case 'each': return `{{#each ${n.over}}}${writeTemplate(n.kids)}{{/each}}`;
            case 'el': {
                const attrs = Object.entries(n.attrs).map(([k, v]) => ` ${k}="${String(v)}"`).join('');
                return VOID.has(n.tag) ? `<${n.tag}${attrs}>` : `<${n.tag}${attrs}>${writeTemplate(n.kids)}</${n.tag}>`;
            }
        }
    }).join('');
}
// ── The script ──────────────────────────────────────────────────────────────────────────────
/**
 * A card's script: kept whole or dropped whole. It runs only in a sandboxed frame with no network
 * (the SDK's `CardScriptFrame`), so what it says is not read here — only that it cannot end the
 * `<script>` it is written into, or open a comment that would swallow the frame's own code.
 */
export function cleanCardScript(raw) {
    if (raw.length > CARD_MARKUP_LIMITS.script)
        return { script: '', stripped: [{ part: 'script', what: `${raw.length} characters`, why: 'too_big' }] };
    if (/<\/script|<!--|<script/i.test(raw))
        return { script: '', stripped: [{ part: 'script', what: quote(/<\/script|<!--|<script/i.exec(raw)[0]), why: 'script_tag' }] };
    return { script: raw, stripped: [] };
}
// ── Forms ───────────────────────────────────────────────────────────────────────────────────
/**
 * The modules no card form may write to: identity's own (people, access, the audit trail, privacy,
 * settings, the parties register, media) and the ones that hold keys or the platform's wiring. The
 * shell checks the unit too (identity's unit is refused whatever a module is called); this is the
 * check that runs where no registry is at hand.
 */
export const FORM_DENIED_MODULES = new Set([
    'identity', 'audit', 'activity_log', 'privacy', 'settings', 'media', 'parties', 'platform', 'registry', 'tenant',
    'extensions', 'notifications', 'connections', 'integrations', 'assistant', 'chat',
]);
/** Words that mark a screen or an action as about access, keys or the system itself. */
const FORM_DENIED_WORDS = new Set([
    'secret', 'secrets', 'token', 'tokens', 'password', 'passwords', 'credential', 'credentials', 'key', 'keys', 'apikey',
    'role', 'roles', 'permission', 'permissions', 'grant', 'grants', 'member', 'members', 'invite', 'invites', 'invitation',
    'session', 'sessions', 'auth', 'sso', 'oauth', 'mfa', 'webhook', 'webhooks', 'integration', 'integrations', 'connection',
    'connections', 'admin', 'impersonate', 'backup', 'backups', 'purge', 'erase', 'delete', 'remove',
]);
/** Why a form's target is refused where it is kept, or null. The shell checks the rest against the action itself. */
export function formTargetProblem(target) {
    if (!ACTION.test(target))
        return 'target';
    const [module, ...rest] = target.split('.');
    if (FORM_DENIED_MODULES.has(module))
        return 'target';
    if ([module, ...rest].some((seg) => seg.split('_').some((w) => FORM_DENIED_WORDS.has(w))))
        return 'target';
    return null;
}
/**
 * The card with its markup read: what survived, and what was dropped. The assistant strips a
 * proposal with this and tells the person; `validateHomeCard` refuses a card that would lose
 * anything here, so what identity keeps and the SDK draws is already clean.
 */
export function cleanCardMarkup(card) {
    const stripped = [];
    const out = { ...card };
    const forms = Array.isArray(card.forms) ? card.forms : [];
    const seen = new Set();
    const keptForms = forms.filter((f) => {
        const target = typeof f?.action === 'string' ? f.action : '';
        const why = seen.has(target) ? 'duplicate' : formTargetProblem(target);
        if (why) {
            stripped.push({ part: 'forms', what: quote(target || 'form'), why });
            return false;
        }
        seen.add(target);
        return true;
    });
    if (Array.isArray(card.forms)) {
        if (keptForms.length)
            out['forms'] = keptForms;
        else
            delete out['forms'];
    }
    if (typeof card.style === 'string') {
        const r = cleanCardStyle(card.style);
        stripped.push(...r.stripped);
        if (r.css)
            out['style'] = r.stripped.length ? r.css : card.style;
        else
            delete out['style'];
    }
    if (typeof card.template === 'string') {
        const r = cleanCardTemplate(card.template, String(card.shape ?? ''), keptForms.map((f) => String(f.action)));
        stripped.push(...r.stripped);
        // Kept as written when nothing was dropped; otherwise written back from what survived.
        if (!r.nodes.length)
            delete out['template'];
        else if (r.stripped.length)
            out['template'] = writeTemplate(r.nodes);
    }
    if (typeof card.script === 'string') {
        const r = cleanCardScript(card.script);
        stripped.push(...r.stripped);
        if (!r.script)
            delete out['script'];
    }
    if (card.sources !== undefined && typeof out['script'] !== 'string') {
        // Extra sources are for a script's reads; with no script nothing reads them.
        stripped.push({ part: 'sources', what: Object.keys(card.sources).join(', ').slice(0, 60) || 'sources', why: 'source' });
        delete out['sources'];
    }
    return { card: out, stripped };
}
//# sourceMappingURL=card-markup.js.map